/* eslint-disable no-undef */

import React, { PureComponent } from 'react'
import { withStyles } from '@material-ui/core/styles/index'
import Terms from './views/partials/Terms'
import PageHeader from './views/partials/PageHeader'
import QuestionPageWrapper from './views/QuestionPageWrapper'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import InfoCards from './views/partials/InfoCards'
import queryString from 'query-string'
import { Button, Grid, Paper, Box, Typography, TextField } from '@material-ui/core'
import Research from './views/partials/Research'
import TrustBar from './components/TrustBar'

// Import the new design system
import './styles/design-system.css'
import {fetchResearches} from "../api";

// Enhanced styles specifically for FullPageQuestionApplication (layout='full')
const fullPageStyles = theme => ({
    root: {
        marginTop: theme.root ? theme.root.marginTop : 0,
        [theme.breakpoints.up(600)]: {
            paddingBottom: theme.spacing(8)
        },
        // Enhanced gradient background for full layout
        background: 'linear-gradient(180deg, #ffffff 0%, #f0f5f3 100%)',
        backgroundColor: '#f8faf9',
        minHeight: '100vh',
        fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important"
    },
    container: {
        maxWidth: 960,
        margin: 'auto',
        [theme.breakpoints.up(600)]: {
            paddingLeft: 16,
            paddingRight: 16
        }
    },
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        background: '#ffffff',
        fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important",
        overflow: 'hidden'
    },
    paper: {
        padding: theme.spacing(2.5),
        paddingBottom: theme.spacing(1) + 'px!important',
        borderRadius: '24px!important',
        background: '#ffffff!important',
        boxShadow: `
            0 8px 32px rgba(32, 90, 64, 0.12),
            0 16px 48px rgba(32, 90, 64, 0.08),
            0 0 0 1px rgba(32, 90, 64, 0.06) inset,
            0 0 60px rgba(32, 90, 64, 0.05)
        `,
        [theme.breakpoints.up(600)]: {
            padding: theme.spacing(4),
            paddingTop: theme.spacing(4) + 'px!important',
            paddingBottom: theme.spacing(1) + 'px!important',
            borderRadius: '28px!important'
        },
        paddingTop: theme.spacing(3) + 'px!important'
    },
    hideOnMobile: {
        [theme.breakpoints.down(600)]: {
            display: 'none'
        }
    },
    infoCardWrapper: {
        paddingLeft: 32,
        [theme.breakpoints.down(600)]: {
            paddingTop: 16,
            paddingLeft: 0
        }
    },
    fullPageDivider: {
        width: 'calc(100% + 4px)',
        marginLeft: -2,
        height: 32,
        background: 'rgba(0,0,0,0.1)',
        marginTop: theme.spacing(2.5),
        marginBottom: theme.spacing(1)
    }
});

// Custom styled components for this page
const pageStyles = {
    // Typography - using direct CSS properties for inline styles
    pageTitle: {
        fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif",
        fontSize: '1.85rem',
        fontWeight: 700,
        color: '#1a2e23',
        lineHeight: 1.25,
        marginTop: 24,
        marginBottom: 20,
        letterSpacing: '-0.02em',
        display: 'block'
    },
    pageSubtitle: {
        fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif",
        fontSize: '1.1rem',
        fontWeight: 600,
        color: '#1a2e23',
        lineHeight: 1.3,
        marginBottom: 12,
        display: 'block'
    },
    bodyText: {
        fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif",
        fontSize: '0.95rem',
        color: '#4a5d52',
        lineHeight: 1.7,
        marginBottom: 16,
        display: 'block'
    },
    // Form styling
    formPaper: {
        padding: 24,
        borderRadius: 20,
        background: '#ffffff',
        boxShadow: '0 4px 20px rgba(32, 90, 64, 0.08)',
        border: '1px solid rgba(32, 90, 64, 0.06)'
    },
    formTitle: {
        fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif",
        fontSize: '1.15rem',
        fontWeight: 600,
        color: '#1a2e23',
        margin: 0,
        marginBottom: 4
    },
    formHelper: {
        fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif",
        fontSize: '0.88rem',
        color: '#6a7d72',
        margin: 0,
        marginBottom: 16
    },
    successMessage: {
        fontSize: '0.95rem',
        fontWeight: 600,
        color: '#10b981',
        background: 'rgba(16, 185, 129, 0.1)',
        padding: '10px 14px',
        borderRadius: 10,
        marginBottom: 12
    },
    textField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: '0.95rem',
            '& fieldset': {
                borderColor: 'rgba(32, 90, 64, 0.15)'
            },
            '&:hover fieldset': {
                borderColor: 'rgba(32, 90, 64, 0.3)'
            },
            '&.Mui-focused fieldset': {
                borderColor: '#205a40',
                borderWidth: 2
            }
        },
        '& .MuiInputLabel-root': {
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: '0.9rem',
            color: '#6a7d72'
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: '#205a40'
        }
    },
    submitButton: {
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontSize: '0.95rem',
        fontWeight: 600,
        textTransform: 'none',
        padding: '12px 24px',
        borderRadius: 12,
        background: 'linear-gradient(135deg, #205a40 0%, #2d7a58 100%)',
        boxShadow: '0 4px 12px rgba(32, 90, 64, 0.25)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease'
    },
    // Loading spinner
    loadingWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 20px',
        gap: 16
    },
    loadingSpinner: {
        width: 48,
        height: 48,
        borderRadius: '50%',
        border: '3px solid rgba(32, 90, 64, 0.1)',
        borderTopColor: '#205a40',
        animation: 'spin 0.8s linear infinite'
    },
    loadingText: {
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontSize: '0.9rem',
        color: '#6a7d72',
        fontWeight: 500
    },
    // CTA Button
    ctaButton: {
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontSize: '1rem',
        fontWeight: 600,
        textTransform: 'none',
        padding: '14px 28px',
        borderRadius: 14,
        background: 'linear-gradient(135deg, #205a40 0%, #2d7a58 100%)',
        boxShadow: '0 4px 16px rgba(32, 90, 64, 0.3)',
        marginTop: 12,
        marginBottom: 20
    },
    // Section headers
    sectionTitle: {
        fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif",
        fontSize: '1.25rem',
        fontWeight: 600,
        color: '#1a2e23',
        margin: 0,
        marginBottom: 20,
        marginTop: 32
    },
    // Steps list
    stepText: {
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontSize: '0.95rem',
        color: '#4a5d52',
        lineHeight: 1.65,
        marginBottom: 16
    },
    stepBold: {
        fontWeight: 600,
        color: '#1a2e23'
    }
}

// CSS keyframes for spinner (injected via style tag)
const spinnerKeyframes = `
@keyframes spin {
    to { transform: rotate(360deg); }
}
`

class FullPageQuestionApplication extends PureComponent {
    state = {
        baseUrl: null,
        contentAlias: null,
        message: '',
        phone: '',
        email: '',
        messageSent: false,
        researches: []
    }

    componentDidMount() {
        const parsedQueryString = queryString.parse(window.location.search)
        const contentAlias = parsedQueryString && parsedQueryString['ca'] ? parsedQueryString['ca'] : null

        delete parsedQueryString.ca
        const baseUrl = window.location.pathname + '?' + queryString.stringify(parsedQueryString)

        this.setState({ baseUrl: baseUrl, contentAlias: contentAlias })

        // Fetch researches for TrustBar and Research component
        fetchResearches().then(response => {
            if (response.data) {
                this.setState({ researches: response.data })
            }
        })

        // Inject spinner keyframes
        if (!document.getElementById('spinner-keyframes')) {
            const style = document.createElement('style')
            style.id = 'spinner-keyframes'
            style.textContent = spinnerKeyframes
            document.head.appendChild(style)
        }
    }

    handleChange = event => {
        this.setState({ [event.target.name]: event.target.value, messageSent: false })
    }

    onSubmitMessage = () => {
        const { message, phone, email } = this.state
        axios
            .post(`/contact`, { message, phone, email })
            .then(res => {
                this.setState({ messageSent: true, message: '', phone: '', email: '' })
            })
            .catch(res => {
                alert('Error on sending the message. You can contact us by email: contact@leadspark.io')
            })
    }

    render() {
        const {
            classes,
            layout,
            questionPage,
            onNext,
            childNext,
            childPrev,
            pageAlias,
            isLoading,
            activeStep,
            visitorSessionId,
            theme,
            previewQuestionPages
        } = this.props

        const { baseUrl, contentAlias, message, phone, email, messageSent, researches } = this.state

        // Get latest winner for TrustBar
        const latestWinner = researches.length > 0 ? researches[0] : null

        const renderContentTutkimukset = (
            <Grid container className={classes.container}>
                <Grid item xs={12} sm={12}>
                    <Box px={{ xs: 2, md: 0 }}>
                        <h1 style={pageStyles.pageTitle}>
                            Tutkimuksemme
                        </h1>
                        <p style={pageStyles.bodyText}>
                            Tuotamme kattavaa, ajantasaista ja luotettavaa tietoa talouden monimuotoisista ilmiöistä. Tehtävämme on auttaa kuluttajia
                            ja yrityksiä ymmärtämään paremmin taloudellista ympäristöä, jossa he toimivat, ja tekemään tietoon perustuvia päätöksiä.
                        </p>
                        <p style={pageStyles.bodyText}>
                            Palvelumme kattaa laajan kirjon taloustieteellisiä tutkimusalueita, mukaan lukien kuluttajakäyttäytymisen ja
                            markkinatrendien analysoinnin. Tutustu toimintaamme ja teetä oma tutkimuksesi ottamalla yhteyttä!
                        </p>
                        <a href={baseUrl}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                style={pageStyles.ctaButton}
                            >
                                Osallistu nykyiseen tutkimukseemme
                                <FontAwesomeIcon style={{ marginLeft: 10 }} size="lg" icon={['far', 'long-arrow-alt-right']} />
                            </Button>
                        </a>
                        <Research showSlogansLast researches={researches} />
                    </Box>
                </Grid>
            </Grid>
        )

        const renderContentTeetaOmaTutkimus = (
            <Grid container className={classes.container}>
                <Grid item xs={12} sm={6}>
                    <Box px={{ xs: 2, md: 0 }}>
                        <h1 style={pageStyles.pageTitle}>
                            Teetä oma tutkimus
                        </h1>
                        <p style={pageStyles.bodyText}>
                            Haluatko ymmärtää paremmin markkinoita, kuluttajien käyttäytymistä tai talouden trendejä? Tutkimuskeskus tarjoaa
                            ainutlaatuisen ja innovatiivisen alustan, jonka avulla voit itse teettää tutkimuksia ja saada arvokasta tietoa
                            liiketoimintasi tai tutkimuksesi tueksi.
                        </p>
                        <p style={pageStyles.bodyText}>
                            Teknologiamme on suunniteltu olemaan käyttäjäystävällinen, joustava ja tehokas. Se mahdollistaa monenlaisten
                            tutkimusten toteuttamisen, oli kyseessä sitten markkinoiden segmentointi, hinta-analyysi, kuluttajatyytyväisyyden
                            mittaus tai taloudellisen kehityksen seuranta. Voit mukauttaa tutkimuksesi juuri sinun tarpeidesi mukaiseksi.
                        </p>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Box px={{ xs: 2, md: 0 }} pb={4} pl={{ xs: 2, md: 4 }}>
                        <Paper style={pageStyles.formPaper} elevation={0}>
                            <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
                                <h2 style={pageStyles.formTitle}>Ota yhteyttä</h2>
                                {messageSent && (
                                    <Box style={pageStyles.successMessage}>
                                        ✓ Viesti lähetetty onnistuneesti!
                                    </Box>
                                )}
                                <p style={pageStyles.formHelper}>
                                    Vastaamme kahden arkipäivän kuluessa.
                                </p>
                                <TextField
                                    variant="outlined"
                                    label="Sähköpostiosoite"
                                    name="email"
                                    onChange={this.handleChange}
                                    value={email}
                                    style={pageStyles.textField}
                                    InputProps={{ style: { borderRadius: 12, fontFamily: "'Plus Jakarta Sans', sans-serif" } }}
                                    InputLabelProps={{ style: { fontFamily: "'Plus Jakarta Sans', sans-serif" } }}
                                />
                                <TextField
                                    variant="outlined"
                                    label="Puhelinnumero"
                                    name="phone"
                                    onChange={this.handleChange}
                                    value={phone}
                                    InputProps={{ style: { borderRadius: 12, fontFamily: "'Plus Jakarta Sans', sans-serif" } }}
                                    InputLabelProps={{ style: { fontFamily: "'Plus Jakarta Sans', sans-serif" } }}
                                />
                                <TextField
                                    variant="outlined"
                                    label="Viesti"
                                    multiline
                                    rows={5}
                                    name="message"
                                    onChange={this.handleChange}
                                    value={message}
                                    InputProps={{ style: { borderRadius: 12, fontFamily: "'Plus Jakarta Sans', sans-serif" } }}
                                    InputLabelProps={{ style: { fontFamily: "'Plus Jakarta Sans', sans-serif" } }}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    style={{ ...pageStyles.submitButton, marginTop: 8, marginLeft: 'auto' }}
                                    onClick={this.onSubmitMessage}
                                >
                                    Lähetä viesti
                                    <FontAwesomeIcon style={{ marginLeft: 10 }} size="lg" icon={['far', 'paper-plane']} />
                                </Button>
                            </Box>
                        </Paper>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={12}>
                    <Box px={{ xs: 2, md: 0 }} pb={4}>
                        <h2 style={pageStyles.sectionTitle}>
                            Kuinka Aloittaa?
                        </h2>
                        <p style={pageStyles.stepText}>
                            <span style={pageStyles.stepBold}>1. Ota yhteyttä, Rekisteröidy ja Luo Profiili:</span> Aloita ottamalla yhteyttä, rekisteröitymällä palveluumme ja luomalla
                            käyttäjäprofiili. Täytä tarvittavat tiedot ja kerro meille, minkä tyyppistä tutkimusta olet kiinnostunut tekemään.
                        </p>
                        <p style={pageStyles.stepText}>
                            <span style={pageStyles.stepBold}>2. Määrittele tutkimuksen tavoitteet ja parametrit:</span> Seuraavaksi sinun tulee määritellä tutkimuksesi tavoitteet ja
                            asettaa tarvittavat parametrit. Alustamme auttaa sinua ymmärtämään, mitkä tekijät ovat oleellisia juuri sinun
                            tutkimuksellesi, ja ohjaa sinua valitsemaan oikeat muuttujat ja mittarit.
                        </p>
                        <p style={pageStyles.stepText}>
                            <span style={pageStyles.stepBold}>3. Valitse datalähteet ja metodologia:</span> Valitse laajasta tietokannastamme ne datalähteet, jotka sopivat parhaiten tutkimuksesi
                            tarpeisiin. Voit myös tuoda omia datalähteitäsi. Valitse sitten sopiva analyysimetodologia. Jos et ole varma, mikä
                            menetelmä sopisi parhaiten, asiantuntijatiimimme on valmis auttamaan ja neuvomaan sinua.
                        </p>
                        <p style={pageStyles.stepText}>
                            <span style={pageStyles.stepBold}>4. Suorita analyysi ja tulkitse tulokset:</span> Kun kaikki on valmista, voit suorittaa analyysin. Teknologiamme käsittelee
                            tiedot nopeasti ja tehokkaasti, ja saat tulokset käyttöösi selkeässä ja visuaalisesti houkuttelevassa muodossa. Opi
                            tulkkaamaan tuloksia ja ymmärtämään, mitä ne tarkoittavat liiketoimintasi tai tutkimuksesi kannalta.
                        </p>
                        <p style={pageStyles.stepText}>
                            <span style={pageStyles.stepBold}>5. Jaa ja hyödynnä tuloksia:</span> Lopuksi voit jakaa tulokset tiimisi tai sidosryhmiesi kanssa ja käyttää niitä päätöksenteon
                            tukena. Tulokset ovat myös tallennettuina tilillesi, joten voit palata niihin myöhemmin tarvittaessa.
                        </p>
                    </Box>
                </Grid>
            </Grid>
        )

        // Trust bar data from question page or defaults
        const trustBarData = {
            estimatedTime: questionPage?.estimatedTime || '2 min',
            trustText: questionPage?.trustText || null
        }

        return (
            <div className={classes.root + ' questionApplicationRoot layout-' + layout + ' tk-app tk-layout'} key={'questionApp'}>
                <PageHeader questionPage={questionPage} baseUrl={baseUrl} />
                <TrustBar
                    estimatedTime={trustBarData.estimatedTime}
                    trustText={trustBarData.trustText}
                    latestWinner={latestWinner}
                />
                {contentAlias ? (
                    <>
                        {contentAlias === 'tutkimukset' && renderContentTutkimukset}
                        {contentAlias === 'teeta-oma-tutkimus' && renderContentTeetaOmaTutkimus}
                    </>
                ) : (
                    <>
                        <Grid container className={classes.container}>
                            <Grid item xs={12} sm={8}>
                                <Paper className={classes.wrapper + ' ' + classes.paper + ' questionApplicationWrapper tk-card'} elevation={0}>
                                    {!questionPage && (
                                        <div style={pageStyles.loadingWrapper}>
                                            <div style={pageStyles.loadingSpinner} />
                                            <span style={pageStyles.loadingText}>Ladataan tutkimusta...</span>
                                        </div>
                                    )}
                                    {!previewQuestionPages && questionPage && (
                                        <QuestionPageWrapper
                                            questionPage={questionPage}
                                            questionPageRef={questionPage}
                                            pageAlias={pageAlias}
                                            isLoading={isLoading}
                                            activeStep={activeStep}
                                            onNext={onNext}
                                            childNext={childNext}
                                            childPrev={childPrev}
                                            visitorSessionId={visitorSessionId}
                                            theme={theme}
                                            layout={layout}
                                        />
                                    )}
                                    {previewQuestionPages &&
                                        previewQuestionPages.map((previewQuestionPage, key) => (
                                            <React.Fragment key={key}>
                                                <QuestionPageWrapper
                                                    questionPage={previewQuestionPage}
                                                    questionPageRef={this.questionPage}
                                                    pageAlias={pageAlias}
                                                    isLoading={isLoading}
                                                    activeStep={activeStep}
                                                    onNext={this.onNext}
                                                    childNext={this.childNext}
                                                    childPrev={this.childPrev}
                                                    visitorSessionId={visitorSessionId}
                                                    theme={theme}
                                                    layout={layout}
                                                    isPreview={key !== 0}
                                                />
                                                {key !== previewQuestionPages.length - 1 && <Box className={classes.fullPageDivider} />}
                                            </React.Fragment>
                                        ))}
                                </Paper>
                                {questionPage && questionPage.termsPosition === 'below' && (
                                    <Box mx={{ xs: 1, sm: 2 }} mt={2}>
                                        <Terms domain={questionPage.domain} theme={theme} layout={layout} />
                                    </Box>
                                )}
                            </Grid>
                            {questionPage && questionPage.sidebarContentUpper && (
                                <Grid item xs={12} sm={4} className={classes.infoCardWrapper}>
                                    <InfoCards infoCards={questionPage.sidebarContentUpper} />
                                </Grid>
                            )}
                        </Grid>
                        {Boolean(theme?.is_extra_elements_visible) && (
                            <Grid container className={classes.container}>
                                <Grid item xs={12} sm={8}>
                                    <Research researches={researches} />
                                </Grid>
                                {questionPage && questionPage.sidebarContentLower && (
                                    <Grid
                                        item
                                        xs={12}
                                        sm={4}
                                        className={classes.hideOnMobile + ' ' + classes.infoCardWrapper}
                                        style={{ paddingTop: 24 }}
                                    >
                                        <InfoCards infoCards={questionPage.sidebarContentLower} />
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </>
                )}
            </div>
        )
    }
}

export default withStyles(fullPageStyles)(FullPageQuestionApplication)
